// Retake flow for a Bousala attempt.
//
// Cooldown is enforced in the database by a BEFORE INSERT trigger on
// `attempts` which raises `RETAKE_COOLDOWN_ACTIVE` with the unlock timestamp
// in the error DETAIL. The cooldown length lives in
// `site_settings.retake_cooldown_days` — never hardcode it here.
//
// A retake on a COMPLETED attempt inserts a NEW attempt row: the previous
// attempt, its report and its `/r/<share_token>` link stay untouched forever.
// Restarting an attempt that was never completed still resets that row in
// place (nothing to preserve, and only one `in_progress` attempt is allowed
// per client per instrument).

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { RETAKE_COOLDOWN_CODE } from "@/lib/bousala/retake-messages";

const BOUSALA_SLUG = "bousala-v1";
const DEFAULT_COOLDOWN_DAYS = 14;

export { RETAKE_COOLDOWN_CODE };

async function readCooldownDays(): Promise<number> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("site_settings")
    .select("value")
    .eq("key", "retake_cooldown_days")
    .maybeSingle();
  const parsed = Number((data as { value?: string } | null)?.value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_COOLDOWN_DAYS;
}

/** Pulls the ISO unlock timestamp out of the trigger's DETAIL field. */
export function unlockAtFromCooldownError(err: unknown): string | null {
  const e = err as { details?: unknown; detail?: unknown; message?: unknown } | null;
  const raw = String(e?.details ?? e?.detail ?? e?.message ?? "");
  const match = raw.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z?/);
  return match ? match[0] : null;
}

export function isRetakeCooldownError(err: unknown): boolean {
  const e = err as { message?: unknown; code?: unknown } | null;
  return String(e?.message ?? "").includes(RETAKE_COOLDOWN_CODE);
}

async function bousalaContext(supabase: any, userId: string) {
  const { data: profile } = await supabase
    .from("profiles")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (!profile) return null;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: instrument } = await supabaseAdmin
    .from("instruments")
    .select("id")
    .eq("slug", BOUSALA_SLUG)
    .maybeSingle();
  if (!instrument) return null;
  return { clientId: profile.id as string, instrumentId: (instrument as any).id as string };
}

export type RetakeEligibility = {
  canRetake: boolean;
  unlockAt: string | null;
  openAttemptId: string | null;
  cooldownDays: number;
};

/** Whether the current user may start a fresh Bousala attempt right now. */
export const getBousalaRetakeEligibility = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<RetakeEligibility> => {
    const { supabase, userId } = context as { supabase: any; userId: string };
    const cooldownDays = await readCooldownDays();
    const ctx = await bousalaContext(supabase, userId);
    if (!ctx) return { canRetake: true, unlockAt: null, openAttemptId: null, cooldownDays };

    const { data: open } = await supabase
      .from("attempts")
      .select("id")
      .eq("client_id", ctx.clientId)
      .eq("instrument_id", ctx.instrumentId)
      .eq("status", "in_progress")
      .limit(1)
      .maybeSingle();

    const { data: latest } = await supabase
      .from("attempts")
      .select("completed_at")
      .eq("client_id", ctx.clientId)
      .eq("instrument_id", ctx.instrumentId)
      .not("completed_at", "is", null)
      .order("completed_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    let unlockAt: string | null = null;
    if (latest?.completed_at) {
      const unlock = new Date(new Date(latest.completed_at).getTime() + cooldownDays * 86400000);
      if (unlock.getTime() > Date.now()) unlockAt = unlock.toISOString();
    }

    return {
      canRetake: !unlockAt,
      unlockAt,
      openAttemptId: (open?.id as string | undefined) ?? null,
      cooldownDays,
    };
  });

export const retakeBousalaAttempt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z
      .object({ attemptId: z.string().uuid(), lang: z.enum(["ar", "en"]).optional() })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as { supabase: any; userId: string };
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!profile) throw new Error("No profile");

    const { data: previous } = await supabase
      .from("attempts")
      .select("id, instrument_id, client_id, completed_at, language, tier, pre_profile")
      .eq("id", data.attemptId)
      .maybeSingle();
    if (!previous || previous.client_id !== profile.id) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const nowIso = new Date().toISOString();
    const initialProgress = { currentSection: "pre", currentIndex: 0 };

    // Never completed → nothing to preserve, reset this row in place. This also
    // respects the "one in_progress attempt" unique index.
    if (!previous.completed_at) {
      const { error: uErr } = await supabaseAdmin
        .from("attempts")
        .update({
          status: "in_progress",
          answers: {},
          pre_profile: {},
          scores: null,
          validity: null,
          confidence: null,
          completed_at: null,
          section_progress: initialProgress,
          started_at: nowIso,
        })
        .eq("id", previous.id);
      if (uErr) throw uErr;
      return { id: previous.id as string, reused: true };
    }

    // An unfinished attempt already exists → continue it instead of inserting.
    const { data: open } = await supabase
      .from("attempts")
      .select("id")
      .eq("client_id", profile.id)
      .eq("instrument_id", previous.instrument_id)
      .eq("status", "in_progress")
      .limit(1)
      .maybeSingle();
    if (open?.id) return { id: open.id as string, reused: true };

    // Fresh attempt. The DB trigger is the authority on the cooldown.
    const { data: created, error: iErr } = await supabaseAdmin
      .from("attempts")
      .insert({
        client_id: profile.id,
        instrument_id: previous.instrument_id,
        status: "in_progress",
        tier: previous.tier ?? "full",
        answers: {},
        pre_profile: {},
        section_progress: initialProgress,
        language: data.lang ?? previous.language ?? "ar",
        started_at: nowIso,
      })
      .select("id")
      .single();

    if (iErr) {
      if (isRetakeCooldownError(iErr)) {
        // Encode the unlock timestamp in the message: extra Error props do not
        // survive the server-function boundary.
        const unlockAt = unlockAtFromCooldownError(iErr);
        throw new Error(`${RETAKE_COOLDOWN_CODE}${unlockAt ? `:${unlockAt}` : ""}`);
      }
      throw iErr;
    }
    return { id: created.id as string, reused: false };
  });
